import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet } from "react-native";
import { Layout } from "~/components/Layout";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ButtonCircle } from "~/components/V2/ButtonCircle";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { MyPetListCard } from "~/components/V2/PetCard";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { TabCard } from "~/components/V2/TabCard";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useStore } from "~/mobx/utils/useStore";
import { assert } from "~/utils/assert";
import { HomeScreenContainer } from "../../components/V2/HomeScreenContainer";

const S = StyleSheet.create({
  welcomeText: {
    lineHeight: 33,
  },
});

export const HomeScreen = observer(function HomeScreen({
  myPetsNames,
}: {
  myPetsNames: string[];
}) {
  const store = useStore();
  const activeUser = store.authStore.activeUser;

  assert(activeUser, "Active user should be defined");

  return (
    <Screen withBottomInset withTopInset preventScroll>
      <Layout>
        <HomeScreenContainer
          topBarContent={
            <>
              <View flexDirectionRow alignItemsCenter>
                <View flex>
                  <BrandText
                    sizeH2
                    colorWhite
                    weightExtraBold
                    style={S.welcomeText}
                  >
                    <FormattedMessage
                      id="jA1SyB"
                      defaultMessage="Welcome{newLine}{name}"
                      description="Welcome message with user's name"
                      values={{
                        name: activeUser?.firstName,
                        newLine: "\n",
                      }}
                    />
                  </BrandText>
                </View>
              </View>
              <View absoluteTopRightExtraLarge>
                <ButtonCircle
                  size="medium"
                  iconName="settings"
                  onPress={() => {
                    router.navigate("/(app)/settings");
                  }}
                />
              </View>
            </>
          }
          mainContent={
            <>
              {/* My Pets Card */}
              <MyPetListCard myPetsNames={myPetsNames} />
              <Spacer large />
              {/* How Can We Help Card */}
              <TabCard />
              <Spacer large />
            </>
          }
        />
      </Layout>
    </Screen>
  );
});

export const HomeScreenWithMyPets = observer(function HomeScreenWithMyPets() {
  const store = useStore();
  const activeUser = store.authStore.activeUser;

  assert(activeUser, "Active user should be defined");

  const query = useQuery({
    queryKey: ["myPets", activeUser.id],
    queryFn: async () => {
      const result = await store.petsStore.getMyPets();
      return result;
    },
  });

  if (query.isLoading) {
    return (
      <Screen withBottomInset withTopInset preventScroll>
        <Layout>
          <HomeScreenContainer
            mainContent={
              <View flex centerContent>
                <SpinnerV2 />
              </View>
            }
          />
        </Layout>
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen withBottomInset withTopInset preventScroll>
        <View flex centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "ZFx66U",
              defaultMessage: "Something went wrong",
              description:
                "Error message title when fetching auth session URL fails",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "T4E2Fv",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching auth session URL",
            })}
          />
        </View>
      </Screen>
    );
  }

  assert(query.data, "Query data should be defined");

  const myPetNames = query.data.map((p) => p.name);

  return <HomeScreen myPetsNames={myPetNames} />;
});
